import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import GetAppIcon from "@material-ui/icons/GetApp";
import FilterListIcon from "@material-ui/icons/FilterList";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import { formatPaymentMethod } from "../../constants/invoiceStatus";

const useStyles = makeStyles((theme) => ({
  widePopover: {
    maxWidth: "none !important",
    minWidth: "340px !important",
  },
  kpiCard: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  kpiValue: {
    fontSize: "2rem",
    fontWeight: 600,
  },
  kpiLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  filterCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
  },
  filterCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    backgroundColor: `${theme.palette.primary.main}0F`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  filterCardTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  filterCount: {
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}24`,
  },
  clearFiltersButton: {
    fontSize: "0.75rem",
  },
  exportButton: {
    marginLeft: "auto",
    fontSize: "0.75rem",
  },
  filterCardBody: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  filterGroupLabel: {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "flex-end",
  },
  rangeBlock: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1, 0.5),
  },
  rangeBlockLabel: {
    display: "block",
    fontSize: "0.6rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  rangePair: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
  },
  rangeDash: {
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(1.25),
  },
  searchField: { minWidth: 240 },
  filterControl: { minWidth: 180 },
  valorField: { width: 120 },
  tableRowClickable: {
    cursor: "pointer",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
}));

const PAGE_LIMIT = 25;

const STATUS_OPTIONS = [
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" },
  { value: "trial", label: "Trial" },
];

const COMPANY_STATUS_OPTIONS = [
  { value: "adimplente", label: "Adimplente" },
  { value: "inadimplente", label: "Inadimplente" },
];

const FORMA_OPTIONS = [
  { value: "CREDIT_CARD", label: "Cartão de Crédito" },
  { value: "PIX", label: "PIX" },
  { value: "BOLETO", label: "Boleto" },
];

const formatBRL = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `R$ ${n.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

const StatusChip = ({ status, plan }) => {
  if (plan?.trial) {
    return (
      <Chip
        size="small"
        label="Trial"
        style={{ backgroundColor: "#fff3e0", color: "#e65100" }}
      />
    );
  }
  return status ? (
    <Chip
      size="small"
      label="Ativo"
      style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
    />
  ) : (
    <Chip
      size="small"
      label="Inativo"
      style={{ backgroundColor: "#ffebee", color: "#c62828" }}
    />
  );
};

const CompanyStatusChip = ({ status }) => {
  if (status === "inadimplente") {
    return (
      <Chip
        size="small"
        label="Inadimplente"
        style={{ backgroundColor: "#ffebee", color: "#c62828" }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label="Adimplente"
      style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
    />
  );
};

const dueDateLabel = (dueDate) => {
  if (!dueDate) return "—";
  const due = moment(dueDate);
  const today = moment().startOf("day");
  const diff = due.startOf("day").diff(today, "days");
  const formatted = due.format("DD/MM/YYYY");
  if (diff < 0) return `${formatted} (venceu há ${Math.abs(diff)}d)`;
  if (diff === 0) return `${formatted} (hoje)`;
  return `${formatted} (em ${diff}d)`;
};

const CompanyView = () => {
  const classes = useStyles();
  const history = useHistory();
  const { list: listPlans } = usePlans();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [kpis, setKpis] = useState({
    active: 0,
    inactive: 0,
    trial: 0,
    totalCompanies: 0,
    activePct: 0,
    pendente: { count: 0, total: 0 },
    vencido: { count: 0, total: 0 },
    pago: { count: 0, total: 0 },
    reembolsado: { count: 0, total: 0 },
    adimplentes: 0,
    inadimplentes: 0,
    adimplentesPct: 0,
  });
  const [pageNumber, setPageNumber] = useState(1);

  const [statusFilter, setStatusFilter] = useState([]);
  const [planFilter, setPlanFilter] = useState([]);
  const [companyStatusFilter, setCompanyStatusFilter] = useState([]);
  const [formaFilter, setFormaFilter] = useState([]);
  const [valorMin, setValorMin] = useState("");
  const [valorMax, setValorMax] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const [planOptions, setPlanOptions] = useState([]);

  // Fetch autocomplete suggestions as the user types (debounced 300ms).
  // Skipped while a company is already selected — picking one locks the
  // filter and clearing the value re-enables suggestions.
  useEffect(() => {
    if (selectedCompany) return undefined;
    const term = searchInput.trim();
    if (!term) {
      setSuggestionOptions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const { data } = await api.get("/companies/super", {
          params: { searchParam: term, limit: 10 }
        });
        setSuggestionOptions(data.rows || []);
      } catch (err) {
        // silent — suggestion fetch failures shouldn't surface as toasts
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, selectedCompany]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, planFilter, companyStatusFilter, formaFilter, valorMin, valorMax, selectedCompany]);

  // Load plan dropdown options
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const plans = await listPlans();
        if (active) setPlanOptions(plans || []);
      } catch (err) {
        if (active) toastError(err);
      }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies/super", {
        params: {
          pageNumber,
          limit: PAGE_LIMIT,
          status: statusFilter.length > 0 ? statusFilter.join(",") : undefined,
          planId: planFilter.length > 0 ? planFilter.join(",") : undefined,
          companyStatus: companyStatusFilter.length > 0 ? companyStatusFilter.join(",") : undefined,
          paymentFlow: formaFilter.length > 0 ? formaFilter.join(",") : undefined,
          valorMin: valorMin || undefined,
          valorMax: valorMax || undefined,
          companyId: selectedCompany?.id || undefined,
        },
      });
      setRows(data.rows || []);
      setCount(data.count || 0);
      setHasMore(!!data.hasMore);
      setKpis(
        data.kpis || { active: 0, inactive: 0, trial: 0, totalCompanies: 0, activePct: 0 }
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, statusFilter, planFilter, companyStatusFilter, formaFilter, valorMin, valorMax, selectedCompany]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Exports every row matching the active filters, not just the current
  // page — the list endpoint caps `limit` at 100, so this pages through
  // it until hasMore is false instead of requesting one giant page.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const filterParams = {
        status: statusFilter.length > 0 ? statusFilter.join(",") : undefined,
        planId: planFilter.length > 0 ? planFilter.join(",") : undefined,
        companyStatus: companyStatusFilter.length > 0 ? companyStatusFilter.join(",") : undefined,
        paymentFlow: formaFilter.length > 0 ? formaFilter.join(",") : undefined,
        valorMin: valorMin || undefined,
        valorMax: valorMax || undefined,
        companyId: selectedCompany?.id || undefined,
      };
      let allRows = [];
      let page = 1;
      let more = true;
      while (more) {
        const { data } = await api.get("/companies/super", {
          params: { ...filterParams, pageNumber: page, limit: 100 },
        });
        allRows = allRows.concat(data.rows || []);
        more = !!data.hasMore;
        page += 1;
      }

      const headers = [
        "Empresa",
        "E-mail",
        "Plano",
        "Status",
        "Vencimento",
        "Status da Empresa",
        "Forma",
        "Último valor faturado",
        "Pagamentos no prazo",
        "Pagamentos Pendentes",
        "Pagamentos Atrasados",
      ];
      // Date fields are wrapped as ="DD/MM/YYYY" so Excel treats them as
      // literal text instead of silently retyping them as Date cells and
      // showing ####### when the column is narrower than the formatted value.
      const csvRows = allRows.map((row) => [
        row.name || "",
        row.email || "",
        row.plan?.name || "",
        row.plan?.trial ? "Trial" : row.status ? "Ativo" : "Inativo",
        row.dueDate ? `="${moment(row.dueDate).format("DD/MM/YYYY")}"` : "",
        row.companyStatus === "inadimplente" ? "Inadimplente" : "Adimplente",
        formatPaymentMethod(row.paymentFlow, row.paymentMethod),
        row.lastPaidValue != null
          ? Number(row.lastPaidValue).toFixed(2).replace(".", ",")
          : "",
        row.paymentCounts?.onTime ?? 0,
        row.paymentCounts?.pending ?? 0,
        row.paymentCounts?.overdue ?? 0,
      ]);
      const csv = [headers, ...csvRows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"))
        .join("\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financeiro-empresas-${moment().format("YYYY-MM-DD")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_LIMIT));

  const activeFilterCount = [
    statusFilter.length > 0,
    planFilter.length > 0,
    companyStatusFilter.length > 0,
    formaFilter.length > 0,
    Boolean(valorMin),
    Boolean(valorMax),
    Boolean(selectedCompany),
  ].filter(Boolean).length;

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" className={classes.kpiCard}>
            <div className={classes.kpiValue} style={{ color: "#2e7d32" }}>
              {kpis.active}
            </div>
            <div className={classes.kpiLabel}>Empresas Ativas</div>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" className={classes.kpiCard}>
            <div className={classes.kpiValue} style={{ color: "#c62828" }}>
              {kpis.inactive}
            </div>
            <div className={classes.kpiLabel}>Empresas Inativas</div>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" className={classes.kpiCard}>
            <div className={classes.kpiValue} style={{ color: "#e65100" }}>
              {kpis.trial}
            </div>
            <div className={classes.kpiLabel}>Empresas Trial</div>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" className={classes.kpiCard}>
            <div className={classes.kpiValue} style={{ color: "#1565c0" }}>
              {kpis.totalCompanies} | {kpis.activePct}%
            </div>
            <div className={classes.kpiLabel}>Total de Empresas | % Ativas</div>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" className={classes.kpiCard}>
            <div className={classes.kpiValue} style={{ color: "#2e7d32" }}>
              {kpis.adimplentes}
            </div>
            <div className={classes.kpiLabel}>Adimplentes ({kpis.adimplentesPct}%)</div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" className={classes.kpiCard}>
            <div className={classes.kpiValue} style={{ color: "#c62828" }}>
              {kpis.inadimplentes}
            </div>
            <div className={classes.kpiLabel}>
              Inadimplentes ({kpis.totalCompanies > 0 ? 100 - kpis.adimplentesPct : 0}%)
            </div>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {[
          { key: "pendente", label: "Pendentes", color: "#e65100" },
          { key: "vencido", label: "Vencidos", color: "#c62828" },
          { key: "pago", label: "Pagos", color: "#2e7d32" },
          { key: "reembolsado", label: "Reembolsados", color: "#ad1457" },
        ].map(({ key, label, color }) => (
          <Grid item xs={12} sm={3} key={key}>
            <Paper variant="outlined" style={{ padding: "16px", display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ flex: 1, textAlign: "center", borderRight: "1px solid #eee", paddingRight: 16 }}>
                <div style={{ fontSize: "1.8rem", fontWeight: 600, color, lineHeight: 1.1 }}>
                  {kpis[key].count}
                </div>
                <div style={{ fontSize: "0.7rem", color: "#9e9e9e", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  pagamentos
                </div>
              </div>
              <div style={{ flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, color }}>
                  {formatBRL(kpis[key].total)}
                </div>
                <div style={{ fontSize: "0.7rem", color: "#9e9e9e", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  total
                </div>
              </div>
            </Paper>
            <Typography variant="caption" style={{ display: "block", textAlign: "center", marginTop: 4, color, fontWeight: 600 }}>
              {label}
            </Typography>
          </Grid>
        ))}
      </Grid>

      <div className={classes.filterCard}>
        <div className={classes.filterCardHeader}>
          <FilterListIcon color="primary" fontSize="small" />
          <Typography className={classes.filterCardTitle}>Filtros</Typography>
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              className={classes.filterCount}
              label={`${activeFilterCount} ativo${activeFilterCount > 1 ? "s" : ""}`}
            />
          )}
          {activeFilterCount > 0 && (
            <Button
              size="small"
              variant="text"
              color="secondary"
              className={classes.clearFiltersButton}
              onClick={() => {
                setStatusFilter([]);
                setPlanFilter([]);
                setCompanyStatusFilter([]);
                setFormaFilter([]);
                setValorMin("");
                setValorMax("");
                setSelectedCompany(null);
                setSearchInput("");
              }}
            >
              Limpar filtros
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            className={classes.exportButton}
            startIcon={exporting ? <CircularProgress size={16} /> : <GetAppIcon />}
            disabled={exporting}
            onClick={exportCsv}
          >
            Exportar CSV
          </Button>
        </div>
        <div className={classes.filterCardBody}>
          <div>
            <div className={classes.filterGroupLabel}>Busca</div>
            <div className={classes.filterRow}>
              <Autocomplete
                size="small"
                className={classes.searchField}
                value={selectedCompany}
                onChange={(_event, newValue) => {
                  setSelectedCompany(newValue);
                  if (!newValue) setSearchInput("");
                }}
                inputValue={searchInput}
                onInputChange={(_event, newInput, reason) => {
                  if (reason === "reset") return;
                  setSearchInput(newInput);
                }}
                options={suggestionOptions}
                loading={suggestionsLoading}
                getOptionLabel={(option) => option?.name || ""}
                getOptionSelected={(option, val) => option.id === val?.id}
                filterOptions={(opts) => opts}
                noOptionsText="Nenhuma empresa encontrada"
                loadingText="Buscando..."
                renderOption={(option) => (
                  <div>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="textSecondary">
                        {option.email}
                      </Typography>
                    )}
                  </div>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Buscar (nome ou e-mail)"
                  />
                )}
              />
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Status de Ativação</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status de Ativação"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => STATUS_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={statusFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Plano</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  label="Plano"
                  notched
                  MenuProps={{ classes: { paper: classes.widePopover } }}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => planOptions.find(p => String(p.id) === String(v))?.name || v).join(", ")
                  }
                >
                  {planOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Checkbox checked={planFilter.some(v => String(v) === String(p.id))} />
                      <ListItemText primary={p.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Status da Empresa</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={companyStatusFilter}
                  onChange={(e) => setCompanyStatusFilter(e.target.value)}
                  label="Status da Empresa"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todos</span>
                      : selected.map(v => COMPANY_STATUS_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {COMPANY_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={companyStatusFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel shrink>Forma</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={formaFilter}
                  onChange={(e) => setFormaFilter(e.target.value)}
                  label="Forma"
                  notched
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: "#9e9e9e" }}>Todas</span>
                      : selected.map(v => FORMA_OPTIONS.find(o => o.value === v)?.label || v).join(", ")
                  }
                >
                  {FORMA_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={formaFilter.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className={classes.rangeBlock}>
                <span className={classes.rangeBlockLabel}>Valor</span>
                <div className={classes.rangePair}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Mín."
                    className={classes.valorField}
                    value={valorMin}
                    onChange={(e) => setValorMin(e.target.value.replace(/[^0-9.,]/g, ""))}
                    inputProps={{ inputMode: "decimal" }}
                  />
                  <span className={classes.rangeDash}>—</span>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Máx."
                    className={classes.valorField}
                    value={valorMax}
                    onChange={(e) => setValorMax(e.target.value.replace(/[^0-9.,]/g, ""))}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Empresa</TableCell>
            <TableCell>Plano</TableCell>
            <TableCell>Status de Ativação</TableCell>
            <TableCell>Vencimento</TableCell>
            <TableCell>Status da Empresa</TableCell>
            <TableCell>Forma</TableCell>
            <TableCell align="right">Pagamentos no prazo</TableCell>
            <TableCell align="right">Pagamentos Pendentes</TableCell>
            <TableCell align="right">Pagamentos Atrasados</TableCell>
            <TableCell align="right">Último valor faturado</TableCell>
          </TableRow>
        </TableHead>
        <TableBody style={{ opacity: loading && rows.length > 0 ? 0.5 : 1, transition: "opacity 0.15s ease" }}>
          {loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Box py={4}>
                  <CircularProgress size={28} />
                </Box>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Box py={4}>
                  <Typography color="textSecondary">
                    Nenhuma empresa encontrada.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                className={classes.tableRowClickable}
                onClick={() => history.push(`/financeiro/empresa/${row.id}`)}
              >
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {row.email || "—"}
                  </Typography>
                </TableCell>
                <TableCell>{row.plan?.name || "—"}</TableCell>
                <TableCell>
                  <StatusChip status={row.status} plan={row.plan} />
                </TableCell>
                <TableCell>{dueDateLabel(row.dueDate)}</TableCell>
                <TableCell>
                  <CompanyStatusChip status={row.companyStatus} />
                </TableCell>
                <TableCell>
                  {formatPaymentMethod(row.paymentFlow, row.paymentMethod)}
                </TableCell>
                <TableCell align="right">{row.paymentCounts?.onTime ?? 0}</TableCell>
                <TableCell align="right">{row.paymentCounts?.pending ?? 0}</TableCell>
                <TableCell align="right">{row.paymentCounts?.overdue ?? 0}</TableCell>
                <TableCell align="right">
                  {row.lastPaidValue != null ? formatBRL(row.lastPaidValue) : "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className={classes.pagination}>
        <Typography variant="body2" color="textSecondary">
          {count > 0
            ? `Mostrando ${(pageNumber - 1) * PAGE_LIMIT + 1}–${
                (pageNumber - 1) * PAGE_LIMIT + rows.length
              } de ${count}`
            : ""}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pageNumber <= 1 || loading}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Typography variant="body2">
            Página {pageNumber} de {totalPages}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasMore || loading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Próxima
          </Button>
        </Box>
      </div>
    </>
  );
};

export default CompanyView;
